import {
    HeroQuestionMarkIconSolid, InformationCircleIcon,
} from '../Icon'
import {
    LoginFormInputs,
    LoginViewProps,
} from './types'
import React, {
    useEffect, useState,
} from 'react'
import {
    otbLabelInner, tooltipText,
} from './styles/LoginView'
import {
    styledFieldset, styledInput, styledLabel, styledSmall,
} from '../Form/styles/FormView'

import AcknowledgeTermsAndConditions from '../AcknowledgeTermsAndConditions'
import Button from '../Button'
import Checkbox from '../Checkbox'

import LoginError from 'components/Errors/Login'
import LoginRegisterHeader from '../LoginRegisterHeader'
import NeedhelpPlaceholder from '../NeedHelpPlaceholder'
import Toggle from 'components/Toggle'
import Tooltip from 'components/Tooltip'
import {errorMessage} from '../LoginRegisterHeader/styles/LoginRegisterHeader'
import {styled} from '../../stitches/config'
import styles from './styles/LoginView.module.scss'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

enum PasswordType {
    basic = 'basic',
    otp = 'otp'
}

const StyledErrorMessage = styled('div', errorMessage)
const StyledTooltipText = styled('div', tooltipText)

const LoginView = (props: LoginViewProps): React.ReactElement => {
    const {
        email,
        password,
        error,
        passwordUpdated,
        isConnecting,
        onForgotPassword,
        hasLoginError,
        onSubmitFormData,
        otp,
        funnelView,
    } = props

    const {
        formState: {errors},
        setFocus,
        register,
        handleSubmit,
    } = useForm<LoginFormInputs>({
        // Using onSubmit since onChange etc. causes problems with autofills
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
    })

    const {t} = useTranslation()
    const [passType, setPassType] = useState(PasswordType.basic)

    useEffect(() => {
        switch (funnelView) {
            case 'password':
                setFocus('password')
                break
            case 'email':
                setFocus('email')
                break
            default:
        }
    }, [funnelView])

    const StyledFieldset = styled('fieldset', styledFieldset)
    const StyledLabel = styled('label', styledLabel)
    const StyledSmall = styled('small', styledSmall)
    const StyledInput = styled('input', styledInput, {
        boxSizing: 'border-box',
        marginBottom: '10px',
        width: '100%',
    })
    const StyledOtpLabelInner = styled('div', otbLabelInner)

    const handleToggleAction = (collapsed: boolean) => {
        setPassType(collapsed ? PasswordType.basic : PasswordType.otp)
    }

    return (
        <div className={styles.LoginView}>
            <div>
                <LoginRegisterHeader />
                {hasLoginError(funnelView, error) &&
                <StyledErrorMessage>
                    <LoginError errorMessage={error} />
                </StyledErrorMessage>}

            </div>
            <form noValidate onSubmit={handleSubmit(onSubmitFormData)}>
                <StyledFieldset hidden={funnelView === 'email' ? undefined : true}>
                    {passwordUpdated ?
                        <StyledErrorMessage type='info'>
                            <div>
                                <InformationCircleIcon />
                                <span>{t('Password updated. You can now sign in.')}</span>
                            </div>
                        </StyledErrorMessage>
                        : null}
                    <StyledLabel htmlFor='emailAddress'>{t('Email')}</StyledLabel>
                    {errors.email ? <StyledSmall error={true} role='alert'>{t('Email address is required')}</StyledSmall> : <StyledSmall>{t('Required')}</StyledSmall>}
                    <StyledInput
                        aria-invalid={errors.email ? 'true' : 'false'}
                        autoComplete='username'
                        defaultValue={email}
                        id='emailAddress'
                        placeholder={t('Email address')}
                        type='email'
                        {...register('email', {required: funnelView === 'email'})}
                    />
                    <Button color='filled' dataTestid='loginContinueButton' disabled={isConnecting} size='large' text={t('Continue')} type='submit' />
                </StyledFieldset>
                <StyledFieldset hidden={funnelView === 'password' ? undefined : true}>
                    <StyledLabel htmlFor='password'>{t('Password')}</StyledLabel>

                    {errors.password ? <StyledSmall error={true} role='alert'>{t('Password is required')}</StyledSmall> : <StyledSmall>{t('Required')}</StyledSmall> }
                    <StyledInput
                        aria-invalid={errors.password ? 'true' : 'false'}
                        autoComplete='current-password'
                        defaultValue={password}
                        id='password'
                        placeholder={t('Password')}
                        type='password'
                        {...register('password', {required: funnelView === 'password'})}
                    />
                    <Toggle
                        // eslint-disable-next-line react/no-unstable-nested-components
                        content={() => (
                            <div>
                                <StyledLabel htmlFor='otp'>
                                    <StyledOtpLabelInner>
                                        {t('2-step verification')}
                                        <span data-tooltip-id='2-step-verification'>
                                            <HeroQuestionMarkIconSolid size={16} />
                                        </span>
                                    </StyledOtpLabelInner>
                                </StyledLabel>
                                {errors.otp ? <StyledSmall error={true} role='alert'>{t('Code is required')}</StyledSmall> : <StyledSmall>{t('Required')}</StyledSmall> }
                                <StyledInput
                                    aria-invalid={errors.otp ? 'true' : 'false'}
                                    autoComplete='otp'
                                    defaultValue={otp}
                                    id='otp'
                                    placeholder={t('Time-based code')}
                                    type='password'
                                    {...register('otp', {required: funnelView === 'password' && passType === PasswordType.otp})}
                                />
                                <br />
                            </div>
                        )}
                        // eslint-disable-next-line react/no-unstable-nested-components
                        header={() => <div>{t('Sign in with OTP')}</div>}
                        isCollapsedByDefault={passType === PasswordType.basic}
                        small={true}
                        toggleHandler={handleToggleAction}
                    />
                    <Tooltip id='2-step-verification' width={200}>
                        <StyledTooltipText>{t('If you have enabled 2-step login, type in a time-based code from your authentication app.')}</StyledTooltipText>
                    </Tooltip>
                    <Checkbox
                        {...register('rememberMe')}
                        id='rememberMe'
                        label={t('Remember me')}
                    />
                    <Button color='filled' disabled={isConnecting} size='large' text={t('Sign in')} type='submit' />
                </StyledFieldset>
            </form>
            <p className='text-center'>
                <Button color='text' dataTestid='forgotPassword' onPress={() => onForgotPassword()} size='large' text={t('Forgot your password?')} />
            </p>
            <p className='text-center'>
                <AcknowledgeTermsAndConditions />
            </p>
            <NeedhelpPlaceholder />
        </div>
    )
}
export default LoginView
